import { Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFieldReadability } from "@/hooks/useFieldReadability";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FieldReadabilityToggleProps {
  variant?: "floating" | "inline";
}

export const FieldReadabilityToggle = ({ variant = "floating" }: FieldReadabilityToggleProps = {}) => {
  const { fieldReadability, toggleFieldReadability } = useFieldReadability();

  if (variant === "inline") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFieldReadability}
              className={cn(
                "rounded-full h-9 w-9",
                fieldReadability
                  ? "bg-[#D6B77A] text-[#1F2A44] hover:bg-[#D6B77A]/90"
                  : "text-foreground hover:bg-accent"
              )}
              aria-label="Toggle Field Readability Mode"
            >
              <Sun className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="font-medium">
              {fieldReadability ? "Disable" : "Enable"} Field Readability
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFieldReadability}
            className={cn(
              "fixed top-4 right-4 z-50 rounded-full shadow-lg md:top-4 md:right-4",
              fieldReadability
                ? "bg-[#D6B77A] text-[#1F2A44] hover:bg-[#D6B77A]/90"
                : "bg-white text-[#1F2A44] hover:bg-white/90 border-2 border-[#D6B77A]/30 dark:bg-[#1F2A44] dark:text-white dark:border-[#D6B77A]"
            )}
            aria-label="Toggle Field Readability Mode"
          >
            <Sun className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p className="font-medium">
            {fieldReadability ? "Disable" : "Enable"} Field Readability
          </p>
          <p className="text-xs text-muted-foreground">
            {fieldReadability ? "Standard view" : "Boost visibility for outdoor work"}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
